/**
 * adapterMatrixInputs — one derivation of everything an offset matrix needs
 * from a loaded adapter.
 *
 * The adapter page and the bank page detect the same collisions over the same
 * addresses, and they are supposed to reach the same verdict. They did not, and
 * the reason was never the detector: it was that each screen assembled the
 * detector's INPUTS for itself, and one of them left an input out.
 *
 * `StepBanks` has two adapter-load paths — one for opening an existing bank, one
 * for picking an adapter while creating a bank. The first passed the adapter's
 * LN1 key mappings to the matrix; the second did not, and nothing said so,
 * because an omitted array is a legal value. With no key mappings the matrix
 * derives no direction for any column, so `columnDirectionsAreOpposed` can never
 * excuse anything: a Control key set TO_DEVICE against a Status key set
 * FROM_DEVICE — legal by design, one written to the controller and one read
 * from it — was excused on the adapter page and flagged on the bank page. The
 * same missing array also cost every column its real byte span, because the
 * span is measured FROM those mappings, so the two screens were sizing the same
 * column differently as well.
 *
 * Adding the missing call would have fixed that instance and left the shape
 * that produced it. This module is the shape fixed: every screen derives its
 * matrix inputs here, in one call, so a new input cannot be wired into one path
 * and forgotten in the other. There is nothing to forget — the inputs arrive
 * together or not at all.
 */
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { messageContentToCategoryKey } from "../../../flatbuffers/types/ln2MessageOptions";
import { LN1_OFFSET_PROTOCOLS } from "../../../constants/ln1OffsetProtocols";
import {
  extractMappedMessageTypes,
  extractModbusRegisterTypeMappedTypes,
  individualColumnKey,
} from "../../setupWizard/steps/BankOffsetMatrix";
import type { AdapterKeyInfo } from "../../setupWizard/steps/BankOffsetMatrix";
import type { MappedMessageType } from "./adapterColumnTypes";
import { deserializeApiKeys } from "./AdapterEditor";
import type { Ln1KeyMappingRow } from "../../setupWizard/steps/CdoLn1MappingEditor";

/**
 * MessageContent discriminant → the category key the offset matrices use.
 *
 * Derived from the generated enum rather than written out, so a category added
 * to the schema appears here without anyone remembering to add it. Four
 * hand-written copies of this table existed — in StepBanks, StepReview,
 * BanksCreateEditForm and BankOffsetMatrix — which is four chances to disagree
 * about what a key belongs to, on the four screens that have to agree.
 *
 * One deliberate divergence from `messageContentToCategoryKey`: the matrices
 * spell Group Supervisory Status in full, while `LN2_MESSAGE_CATEGORIES` spells
 * it `group_supervisory`. The matrix spelling is what every stored column key
 * and every persisted offset map already uses, so it is the one that has to
 * survive; the alias is applied here, once, instead of being re-typed into each
 * copy. `isGroupScopedCategory` matches both spellings for the same reason.
 */
const MATRIX_CATEGORY_KEY_ALIASES: Record<string, string> = {
  group_supervisory: "group_supervisory_status",
};

export const MESSAGE_CONTENT_TO_MATRIX_KEY: Readonly<Record<number, string>> = Object.freeze(
  Object.fromEntries(
    Object.values(MessageContent)
      .filter((v): v is MessageContent => typeof v === "number" && v !== MessageContent.NONE)
      .flatMap((mc) => {
        const key = messageContentToCategoryKey(mc);
        if (!key) return [];
        return [[mc, MATRIX_CATEGORY_KEY_ALIASES[key] ?? key]] as [number, string][];
      }),
  ),
);

/** The matrix category key for a MessageContent, or the raw number as a string
 *  when the enum carries a member no category has been authored for. */
export function matrixCategoryKey(mc: number): string {
  return MESSAGE_CONTENT_TO_MATRIX_KEY[mc] ?? String(mc);
}

/** An adapter as it arrives from the API: FlatBuffer camelCase, legacy JSON
 *  snake_case, or a merge of both. Every reader here accepts either spelling. */
type LooseAdapter = Record<string, any> | null | undefined;

/** Read a field under either spelling, camelCase first. */
function dual<T>(k: Record<string, any>, camel: string, snake: string): T | undefined {
  return (k[camel] ?? k[snake]) as T | undefined;
}

/**
 * The adapter's protocol as the uppercase string the protocol sets are keyed on.
 *
 * `protocolType` is a numeric `AdapterProtocol` on the FlatBuffer path and a
 * string on the legacy JSON path, and the three former copies of this handled
 * that between them: one read only `protocol_type`, one only the string
 * spellings, one did the enum reverse-lookup. An adapter that answered the
 * "wrong" way silently took the non-LN1 branch and got per-key detection it
 * must not have.
 */
export function adapterProtocolName(adapter: LooseAdapter): string {
  const candidates = [
    adapter?.protocol_type,
    adapter?.protocolType,
    // The FlatBuffer Object API and the legacy JSON agree on neither case nor
    // type, and a third PascalCase spelling reaches the bank form. All four are
    // read here rather than at each call site, because a spelling one reader
    // knows and another does not is the shape every bug in this file has had.
    adapter?.ProtocolType,
  ];
  const resolved = candidates.reduce<string>((acc, raw) => {
    if (acc) return acc;
    if (typeof raw === "string" && raw) return raw.toUpperCase();
    if (typeof raw === "number") {
      const name = AdapterProtocol[raw as AdapterProtocol];
      if (name) return String(name).toUpperCase();
    }
    return acc;
  }, "");

  return resolved;
}

/**
 * Extract `AdapterKeyInfo[]` for per-key overlap detection.
 *
 * Returns `[]` for the LN1 family on purpose. Their matrix cells hold ABSOLUTE
 * byte offsets and every key in a category shares one address, so per-key
 * detection sees a point collision between every pair of keys in a category.
 * The empty array routes `detectOverlaps` to the range-based path, which is the
 * correct check for them.
 *
 * A key gets its own column when it is `bank_level` OR `individual` — the same
 * rule `extractMappedMessageTypes` uses to BUILD the own columns. The previous
 * copies tested `bank_level` alone, so an `individual` key was given a column of
 * its own by one function and filed under its plain category by the other, and
 * its offsets were read from a column it does not live in.
 */
export function extractAdapterKeys(adapter: LooseAdapter): AdapterKeyInfo[] {
  if (LN1_OFFSET_PROTOCOLS.has(adapterProtocolName(adapter))) return [];

  const keys: Record<string, any>[] = (adapter?.keys as Record<string, any>[]) ?? [];
  return keys
    .filter((k) => (dual<number>(k, "messageContent", "message_content") ?? 0) > 0)
    .map((k) => {
      const mc = dual<number>(k, "messageContent", "message_content") ?? 0;
      const messageValue = dual<number>(k, "messageValue", "message_value") ?? 0;
      // encoded_type 1 = OneHot (byte), 2 = BitMask (bit)
      const isBit = dual<number>(k, "encodedType", "encoded_type") === 2;
      const ownColumn =
        dual<boolean>(k, "bankLevel", "bank_level") === true ||
        dual<boolean>(k, "individual", "individual") === true;
      const baseCatKey = matrixCategoryKey(mc);
      return {
        categoryKey: ownColumn ? individualColumnKey(k) : baseCatKey,
        byteOffset: dual<number>(k, "position", "position") ?? 0,
        bit: isBit ? messageValue : -1,
        label: `${baseCatKey}::${messageValue}`,
      };
    });
}

/**
 * True when an `intra_content_flags` record has any actual true value.
 *
 * A record of all-false arrays (`{"5": [false, false]}`) is intra-content OFF.
 * Reading its mere presence as ON merged categories that were never meant to
 * share a register.
 */
export function hasAnyIntraContentTrue(flags: unknown): boolean {
  if (!flags || typeof flags !== "object") return false;
  return Object.values(flags as Record<string, unknown>).some((arr) =>
    Array.isArray(arr) ? arr.some(Boolean) : Boolean(arr),
  );
}

/**
 * Per-MessageContent intra-content rollup: which categories have the flag set
 * on at least one unit.
 *
 * `extractMappedMessageTypes` merges only the flagged categories into a shared
 * column, so it needs the per-category answer, not the any-true one.
 */
export function intraContentFlagsByMc(flags: unknown): Record<number, boolean> {
  const byMc: Record<number, boolean> = {};
  if (!flags || typeof flags !== "object" || Array.isArray(flags)) return byMc;
  Object.entries(flags as Record<string, unknown>).forEach(([k, v]) => {
    const mc = Number(k);
    if (!Number.isFinite(mc) || mc <= 0) return;
    byMc[mc] = Array.isArray(v) ? v.some(Boolean) : Boolean(v);
  });
  return byMc;
}

/** Everything an offset matrix needs from one adapter. */
export interface AdapterMatrixInputs {
  /** The matrix's columns. */
  mappedTypes: MappedMessageType[];
  /** Per-key positions for byte+bit detection; `[]` for the LN1 family, which
   *  routes detection to the range-based path on purpose. */
  adapterKeys: AdapterKeyInfo[];
  /** The adapter's LN1 key mappings. Supplies each column's REAL byte span and
   *  each column's direction, so leaving it out silently disables both. */
  ln1KeyMappings: Ln1KeyMappingRow[];
  /** Any-true rollup, for the callers that still take a single boolean. */
  intraContent: boolean;
  /** The per-category, per-unit flags the detector prefers. */
  intraContentFlags: Record<string, boolean[]> | undefined;
  /** MODBUS register-type columns, empty for other protocols. */
  registerTypeMappedTypes: MappedMessageType[];
  /** Category key → MODBUS register type, for the independent-address-space
   *  guard. Empty for other protocols. */
  categoryToRegType: Record<string, string>;
}

/**
 * Derive every matrix input from one loaded adapter.
 *
 * Call this instead of assembling the inputs at the call site. The point is not
 * brevity: it is that the inputs are derived TOGETHER, so a screen cannot end up
 * holding four of the five and behave as though the fifth were empty rather than
 * missing.
 */
export function deriveAdapterMatrixInputs(adapter: LooseAdapter): AdapterMatrixInputs {
  const intraFlagsRaw = adapter?.intra_content_flags ?? adapter?.intraContentFlags;
  const cdoData = adapter?.cdo_template ?? adapter?.cdo_input;
  const regTypes = extractModbusRegisterTypeMappedTypes(cdoData);

  return {
    mappedTypes: extractMappedMessageTypes(cdoData, adapter, intraContentFlagsByMc(intraFlagsRaw)),
    adapterKeys: extractAdapterKeys(adapter),
    ln1KeyMappings: deserializeApiKeys((adapter?.keys as any[]) ?? []),
    intraContent: hasAnyIntraContentTrue(intraFlagsRaw),
    intraContentFlags: (intraFlagsRaw as Record<string, boolean[]>) ?? undefined,
    registerTypeMappedTypes: regTypes.types,
    categoryToRegType: regTypes.categoryToRegType,
  };
}
